import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  resetForgotPasswordApi,
  validateForgotPasswordTokenApi,
} from "../api/api";

export default function ForgotPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [loginId, setLoginId] = useState<number | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) return;

      try {
        setValidating(true);
        const data = await validateForgotPasswordTokenApi(token);
        if (data?.email) setEmail(data.email);
        if (data?.login_id) setLoginId(Number(data.login_id));
      } catch (err: any) {
        setError(err.message || "Reset link is invalid or expired");
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!email.trim()) {
      setError("Email is required.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError("Please enter and confirm your new password.");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!token || !loginId) {
      setError("Reset link is invalid or missing required data.");
      return;
    }

    try {
      setLoading(true);
      await resetForgotPasswordApi(email, loginId, token, newPassword);
      setMessage("Password updated successfully. Redirecting to login...");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err: any) {
      setError(err.message || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f5f7fb",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
          padding: 28,
        }}
      >
        <h2 style={{ marginBottom: 8, fontSize: 24 }}>Forgot Password</h2>
        <p style={{ marginBottom: 20, color: "#64748b" }}>
          Enter your email and set your new password.
        </p>

        {validating ? (
          <div style={{ marginBottom: 12, color: "#2563eb", fontSize: 14 }}>
            Validating reset link...
          </div>
        ) : null}

        

        {error ? (
          <div style={{ marginBottom: 12, color: "#dc2626", fontSize: 14 }}>
            {error}
          </div>
        ) : null}

        {message ? (
          <div style={{ marginBottom: 12, color: "#16a34a", fontSize: 14 }}>
            {message}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} style={{ display: "grid", gap: 14 }}>
          <div>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1" }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1" }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1" }}
            />
          </div>

          <button
            type="submit"
            disabled={loading || validating}
            style={{
              padding: "12px 14px",
              border: "none",
              borderRadius: 8,
              background: loading || validating ? "#94a3b8" : "#2563eb",
              color: "#fff",
              cursor: loading || validating ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            {loading ? "Resetting..." : validating ? "Validating..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
